import { FrontendErrorEventRequestModel, FrontendLoggerLevel } from '@ps20/bff-models';
import { GlobalActions, GlobalEventDispatcher } from '@ps20-frontend/shop-integration-redux';
import { App } from '@ps20-frontend/frontend-models';
import { Dynatrace } from '@ps20-frontend/dynatrace';
import debounce from 'lodash/debounce';
import * as util from './logger.util';

const LOG_DEBOUNCE_MS = 300;
const LOG_DEBOUNCE_MAX_WAIT_MS = 2000;

export interface LoggerConfig {
  error: boolean;
  info: boolean;
  globalEventDispatcherRef: GlobalEventDispatcher | null;
}

export const GLOBAL_LOGGER_WINDOW_ATTRIBUTE = 'ps20Logger';

export class Logger {
  public readonly URL = '/privatschutz/api/frontendlogging';
  private readonly _loggerName: string = GLOBAL_LOGGER_WINDOW_ATTRIBUTE;
  private readonly debouncedInfoSender = debounce(
    (logPayload: FrontendErrorEventRequestModel, consoleText: string) => {
      // Debounce console output to reduce noise when many logs fire quickly.
      // eslint-disable-next-line no-console,no-restricted-syntax
      console.info(consoleText);
      this.sendLogs(logPayload);
    },
    LOG_DEBOUNCE_MS,
    { maxWait: LOG_DEBOUNCE_MAX_WAIT_MS },
  );
  private readonly debouncedErrorSender = debounce(
    (logPayload: FrontendErrorEventRequestModel, consoleText: string) => {
      console.error(consoleText);
      this.sendLogs(logPayload);
    },
    LOG_DEBOUNCE_MS,
    { maxWait: LOG_DEBOUNCE_MAX_WAIT_MS },
  );
  private _config: LoggerConfig = {
    error: true,
    info: true,
    globalEventDispatcherRef: null,
  };

  static getInstance(): Logger {
    if ((window as unknown as Record<string, unknown>)[GLOBAL_LOGGER_WINDOW_ATTRIBUTE] == null) {
      (window as unknown as Record<string, unknown>)[GLOBAL_LOGGER_WINDOW_ATTRIBUTE] = new Logger();
    }

    return (window as unknown as Record<string, unknown>)[GLOBAL_LOGGER_WINDOW_ATTRIBUTE] as Logger;
  }

  set config(loggerConfigObj: LoggerConfig) {
    this._config = loggerConfigObj;
  }

  get name(): string {
    return this._loggerName;
  }

  public error(sourceApp: App, errorOrExceptionText: string | Error, message = ''): void {
    if (!this._config.error) {
      console.error('Logging with error is currently disabled! Check logger config!');
      return;
    }

    if (typeof errorOrExceptionText === 'string') {
      this.logWithErrorObject(
        { name: '', message: errorOrExceptionText, stack: '' },
        sourceApp,
        message,
      );
    } else {
      this.logWithErrorObject(errorOrExceptionText, sourceApp, message);
    }
  }

  public info(exceptionText: string, message: string, sourceApp?: string): void {
    if (this._config.info) {
      const consoleText = `[${sourceApp}]: ${exceptionText}: ${message}`;
      return this.debouncedInfoSender(
        util.createPayload(FrontendLoggerLevel.INFO, sourceApp, exceptionText, message),
        consoleText,
      );
    }
  }

  private sendLogs(logPayload: FrontendErrorEventRequestModel): void {
    if (this._config.globalEventDispatcherRef != null) {
      this._config.globalEventDispatcherRef.dispatch(
        new GlobalActions().doRequest({
          appId: App.SHOP,
          url: this.URL,
          body: logPayload,
          successEvent: GlobalActions.LOGGING_SUCCESSFUL,
          errorEvent: GlobalActions.LOGGING_FAILED,
        }),
      );
    }
  }

  private logWithErrorObject(errorObject: Error, sourceApp: App, message: string): void {
    const exceptionText = `[${sourceApp}]: ${errorObject.name} #stack:${errorObject.stack} @message:${errorObject.message}`;

    Dynatrace.dtReportError(errorObject, sourceApp);
    this.debouncedErrorSender(
      util.createPayload(
        FrontendLoggerLevel.ERROR,
        sourceApp,
        JSON.stringify(exceptionText),
        message,
      ),
      exceptionText,
    );
  }
}

export const PS20Logger = Logger.getInstance();
